import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  shiftStatusQueryKey,
  useShiftStatusQuery,
} from "./use-work-schedule-query";

const NOTICE_LEAD_MS = 60_000;

/** setTimeout overflows past ~24.8 days; the poll re-arms us as the shift nears. */
const MAX_SCHEDULE_AHEAD_MS = 12 * 60 * 60 * 1000;

/** Refetch just after the boundary so the derived flip has definitely landed. */
const FLIP_BUFFER_MS = 1000;

export interface ShiftStartNotice {
  /** Set when a shift starts within the lead window and the user is off shift. */
  readonly startsAt: Date | null;
  readonly dismiss: () => void;
}

/** Warns before a shift begins and refetches at the boundary so the dot flips itself. */
export function useShiftStartNotice({
  enabled,
}: {
  enabled: boolean;
}): ShiftStartNotice {
  const statusQuery = useShiftStatusQuery({ enabled });
  const queryClient = useQueryClient();
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  // Keyed by shift instant so dismissing one notice cannot suppress the next.
  const dismissedFor = useRef<number | null>(null);

  const status = statusQuery.data;
  const nextTransition = status?.nextTransitionAt;
  const active = status?.active;
  const nextActive = status?.nextTransitionActive;

  useEffect(() => {
    setStartsAt(null);
    // Only announce a flip that puts the user on shift, never one ending it.
    if (
      !enabled ||
      nextTransition === undefined ||
      active !== false ||
      nextActive !== true
    ) {
      return;
    }

    const start = nextTransition.getTime();
    const untilStart = start - Date.now();
    if (untilStart <= 0 || untilStart > MAX_SCHEDULE_AHEAD_MS) {
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];
    const openNotice = () => {
      if (dismissedFor.current !== start) {
        setStartsAt(new Date(start));
      }
    };

    const untilNotice = untilStart - NOTICE_LEAD_MS;
    if (untilNotice <= 0) {
      openNotice();
    } else {
      timers.push(setTimeout(openNotice, untilNotice));
    }

    timers.push(
      setTimeout(() => {
        setStartsAt(null);
        void queryClient.invalidateQueries({ queryKey: shiftStatusQueryKey() });
      }, untilStart + FLIP_BUFFER_MS),
    );

    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    };
  }, [enabled, nextTransition, active, nextActive, queryClient]);

  return {
    startsAt,
    dismiss: () => {
      if (startsAt) {
        dismissedFor.current = startsAt.getTime();
      }
      setStartsAt(null);
    },
  };
}
